import { PROBLEM_CODE } from 'apisauce';

import * as i18nTypes from '../core/i18n/types';

export { i18nTypes };

// Enums -------------------------------------------------------------
export enum SendCommunicationCustomersSelection {
  All = 1,
  Ascending = 2,
  Descending = 3,
  AscendingDescending = 4,
  DVNumbers = 5, // equivalent to targetType === TargetType.DVNumbers
}

export enum SendCommunicationChannels {
  Mail = 1,
  Sms = 2,
  WhatsApp = 3,
  FlashInfo = 4,
}

export enum SendCommunicationPurchaseComparisonValues {
  BEFORE = 1,
  AFTER = 2,
}

export enum ServiceTypesGroupingModeValues {
  ET = 1,
  OU = 2,
}

export enum TargetType {
  DV_NUMBERS = 1,
  CLIENT_TYPE = 2,
}
// -------------------------------------------------------------------

/** deprecated */
export interface Credentials {
  userName: string;
  password: string;
}

export interface LoginData {
  expirationDate: string;
}

export interface MinimalResponse {
  message?: string | null;
}

export interface Login {
  userName: string;
  password: string;
}

export type LoginResponse = LoginData & UserInfo;

export interface ForgotPassword {
  email: string;
}

export type RequestError = PROBLEM_CODE | 'ALREADY_EXISTS' | 'unknown';

export type UserAlreadyExistCodeError = 'Email' | 'Username' | 'Email, Username';
export type UserCodeError = UserAlreadyExistCodeError; // add other code errors if needed

interface UserRole {
  role: {
    userRoles: string[] | undefined;
    id: string;
    name: string;
    normalizedName: string;
    concurrencyStamp: string;
  };
  userId: string;
  roleId: string;
}
export interface UserInfo {
  isTemporary: boolean | null;
  firstName: string;
  lastName: string;
  passwordExpiryDate: string;
  userRoles: [UserRole] | null;
  id: string;
  userName: string;
  normalizedUserName: string;
  email: string;
  normalizedEmail: string;
  emailConfirmed: boolean | null;
  passwordHash: string;
  securityStamp: string;
  concurrencyStamp: string;
  phoneNumber: string | null;
  phoneNumberConfirmed: boolean | null;
  twoFactorEnabled: boolean;
  lockoutEnd: string | null;
  lockoutEnabled: boolean | null;
  accessFailedCount: number | string;
  profile: {
    id: number;
    name: string;
    sections: Section[];
  };
  serviceTypeIDs: number[];
}
export interface UserModifyInfo extends MinimalResponse {
  data?: UserInfo | null;
  isCurrentUser?: boolean;
  status?: number;
  errorCode?: UserCodeError;
}

export interface Users {
  result?: User[];
  pageNumber?: number;
  maxResultPage?: number;
  maxPages?: number;
}

export interface User {
  userName?: string;
  firstName?: string;
  profileID?: number | string;
  profile?: string;
  trainTypeIDs?: number[];
  trainTypeName?: string[];
  groupName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  id?: number | string;
  passwordExpiryDate?: string | null;
  creationDate?: string | null;
  modificationDate?: string | null;
  lastLoginDate?: string | null;
}

export type UserParams = Pick<
  User,
  'userName' | 'firstName' | 'lastName' | 'email' | 'groupName' | 'profileID' | 'trainTypeIDs'
>;

export interface ProfilParams {
  id: number;
  name: string;
  sectionsAndPermissions: { id: number; read: boolean; write: boolean }[];
}

export interface Profil {
  id: number;
  isSuperAdmin?: boolean;
  name: string;
  sections: Section[];
}

// @deprected
export interface ProfilLegacy {
  name: string;
  Circulations: string;
  Historisation: string;
  'Customer-Flux': string;
  Communication: string;
  Reasons: string;
  'Deffered-Communications': string;
  'Help-Info': string;
  Typologies: string;
  Situations: string;
  Profiles: string;
  Users: string;
  id: string;
}
export interface CreatedUser extends MinimalResponse {
  data?: UserInfo | null;
  status?: number;
  errorCode?: UserCodeError;
}
export interface Segments {
  /** UicCode */
  departureStation: string;
  /** 3 chars string */
  departureShortCodeStation: string;
  /** full station name, need stations to be fetched */
  departureStationFullName?: string;
  departureTime: string;
  departureSequenceNumber: number;
  arrivalTime: string;
  /** UicCode */
  arrivalStation: string;
  /** 3 chars string */
  arrivalShortCodeStation: string;
  /** full station name, need stations to be fetched */
  arrivalStationFullName?: string;
  arrivalSequenceNumber: number;
  firstTrainNbPassengers: number;
  secondTrainNbPassenger: number;
  isDepartureStopSkip: boolean;
  isArrivalStopSkip: boolean;
}
export interface Services {
  segments: Segments[];
  serviceName: string;
  circulationDate: string;
  hasCommunication: boolean;
  hasDifferedCommunication: boolean;
  isTodaysCirculation: boolean;
  serviceType: string;
  isCanceled: boolean;
  rollingLine?: string;
}
export interface Circulations {
  result?: Services[];
  pageNumber?: number;
  maxResultPage?: number;
  maxPages?: number;
  searchTimestamp?: string;
}

// Customers By OD
export interface CustomersByOD {
  result?: CustomerByODService[];
  pageNumber?: number;
  maxResultPage?: number;
  maxPages?: number;
  searchTimestamp?: string; // managed only in the front (no corresponding param from the back)
}
export interface CustomerByODService {
  serviceName?: string;
  departureTime?: string;
  circulationDate?: string;
  serviceTypeCode?: string | null;
  isCancel?: boolean;
  rollingLine?: string | null;
  fluxClientODs: FluxClientOD[];
}
export interface FluxClientOD {
  departureStationUic: string;
  departureStationShortCode: string;
  departureStationTime: string | null;
  // departureStationSequenceNumber: number; TODO: uncoment if needed
  stationStopTime: string | null;
  allTrainNbPassengersUp: number;
  firstTrainNbPassengersUp: number;
  secondTrainNbPassengersUp: number;
  allTrainChildUp: number;
  firstTrainChildUp: number;
  secondTrainChildUp: number;
  allTrainUfrUp: number;
  firstTrainUfrUp: number;
  secondTrainUfrUp: number;
  allTrainAssistanceUp: number;
  firstTrainAssistanceUp: number;
  secondTrainAssistanceUp: number;
  allTrainBabyUp: number;
  isDepartureStationStopSkip: boolean;
  fluxClientODDown: FluxClientODDown[];
  departureStationFullName?: string; // managed only in the front (no corresponding param from the back)
}
export interface FluxClientODDown {
  arrivalStationUic: string;
  arrivalStationShortCode: string;
  arrivalStationTime: string | null;
  arrivalStationSequenceNumber: number;
  allDepartureNbPassengersDown: number;
  allTrainNbPassengersDown: number;
  firstTrainNbPassengersDown: number;
  secondTrainNbPassengersDown: number;
  allTrainChildDown: number;
  firstTrainChildDown: number;
  secondTrainChildDown: number;
  allTrainUfrDown: number;
  firstTrainUfrDown: number;
  secondTrainUfrDown: number;
  allTrainAssistanceDown: number;
  firstTrainAssistanceDown: number;
  secondTrainAssistanceDown: number;
  allTrainBabyDown: number;
  isArrivalStationStopSkip: boolean;
  arrivalStationFullName?: string; // managed only in the front (no corresponding param from the back)
}

// Customers By Station
export interface CustomersByStation {
  result?: CustomerByStationService[];
  pageNumber?: number;
  maxResultPage?: number;
  maxPages?: number;
  searchTimestamp?: string; // managed only in the front (no corresponding param from the back)
}
export interface CustomerByStationService {
  serviceName?: string;
  departureTime?: string;
  circulationDate?: string;
  serviceTypeCode?: string | null;
  isCancel?: boolean;
  rollingLine?: string | null;
  fluxClientStations: FluxClientStation[];
}
export interface FluxClientStation {
  serviceName: string;
  serviceDate: string;
  stationSequence: number;
  uicStation: string;
  shortCodeStation: string;
  departureTime: string | null;
  arrivalTime: string | null;
  stopTime: string | null;
  firstTrainNbPassengersUp: number;
  firstTrainNbPassengersDown: number;
  firstTrainNbPassengersTotal: number;
  secondTrainNbPassengersUp: number;
  secondTrainNbPassengersDown: number;
  secondTrainNbPassengersTotal: number;
  allTrainNbPassengers: number;
  allTrainUfrUp: number;
  allTrainUfrDown: number;
  allTrainUfrTotal: number;
  allTrainChildUp: number;
  allTrainChildDown: number;
  allTrainChildTotal: number;
  allTrainBabyUp: number;
  allTrainBabyDown: number;
  allTrainBabyTotal: number;
  allTrainAssistanceUp: number;
  allTrainAssistanceDown: number;
  allTrainAssistanceTotal: number;
  isStopSkip: boolean;
  stationFullName?: string; // managed only in the front (no corresponding param from the back)
}
export interface Service {
  serviceName?: string;
  departureStation?: string;
  arrivalStation?: string;
  startDate?: string;
  endDate?: string;
  pageNumber?: number;
  maxResultPage?: number;
}
export type FluxClientsFilters = Service & {
  station?: string;
  includedRolling?: boolean;
  includedCancel?: boolean;
  serviceTypeIDs?: number[];
};

export type GetCirculationFilters = Service & {
  weekdays?: string;
  includedRolling?: boolean;
  includedCancel?: boolean;
  serviceTypeIDs?: number[];
};

export type GetInitializeCommunication = GetCirculationFilters & {
  IsBulkCommunication?: boolean;
};

export type InitializeCommunicationResponse = {
  servicesType: string;
  servicesTypeID: number;
  services: {
    serviceName: string;
    circulationDate: string;
    rollingLine?: string;
  }[];
  equipementCarriages: number[] | null;
  stations: {
    stationShortCode: string;
    stationUicCode: string;
  }[];
  maxDaysBeforeDeferredSending: number;
};

export interface InitializeCommunication {
  isValid: boolean;
  response: InitializeCommunicationResponse | null;
}

export type Callback<A, R> = (...args: A[]) => R;

export interface State {
  status?: 'loading' | 'error' | 'success';
  error?: RequestError;
  errorType?: string;
  message?: string;
}

export type UpdatePasswordValues = {
  userName: string;
  password: string;
  newPassword: string;
  confirmNewPassword: string;
};

export type UpdatePasswordParams = {
  userName: string;
  password: string;
  newPassword: string;
};

export interface PublicStation {
  uicStationCode: string;
  name: string;
  connectedStations: string[];
}

export interface SendStatus {
  id: number;
  name: string;
}

export type LanguageInfo = {
  code: string;
  label: string;
};

export interface ReasonTranslation {
  languageCode: string;
  name: string;
  reasonID: number;
  id: number;
}

export interface Reason {
  id: number;
  name: string;
  isNotEditable?: boolean;
  situationCategory?: {
    id: number;
    name: string;
  };
  translations: ReasonTranslation[];
}

export interface Situation {
  id: number;
  name: string;
  isNotEditable?: boolean;
  situationCategory: {
    id: number;
    name: string;
  };
}

export interface SituationForm {
  id?: number;
  name: string;
  situationCategoryID?: number;
}

/** Mask = situation name + templates */
export interface Mask {
  situationName: string;
  templates: MaskTemplate[];
}

export interface MailConfigurationTranslation {
  languageCode: string;
  fileName: string;
  id: number;
  fileModificationDate: string | null;
  defaultObject: string;
}

export interface MaskTemplate {
  clientType: string;
  clientTypeId: number;
  flashEnabled?: boolean;
  id: number;
  situationId: number;
  mailConfiguration: {
    defaultHeadband: string;
    defaultObject: string;
    enabledByDefault: boolean;
    fileModificationDate: string | null;
    fileName: string | null;
    translations: MailConfigurationTranslation[];
  };
  smsConfiguration: {
    enabledByDefault: boolean;
    fileModificationDate: string | null;
    fileName: string | null;
  };
  whatsappConfiguration: {
    enabledByDefault: boolean;
    maskFileModificationDate: string | null;
    maskFileName: string | null;
    refFileModificationDate: string | null;
    refFileName: string | null;
  };
}

export interface MasksConfig {
  mail?: boolean;
  sms?: boolean;
  whatsApp?: boolean;
  flashInfo?: boolean;
}

export interface UpdateMaskParams {
  id: number;
  mailConfiguration: {
    defaultObject?: string;
    defaultHeadband?: string;
    enabledByDefault: boolean;
    file?: File;
    deleteFile?: boolean;
    deleteDefaultObject?: boolean;
    translations: {
      languageCode: string;
      file?: File;
      deleteFile?: boolean;
      deleteDefaultObject?: boolean;
    }[];
  };
  smsConfiguration: {
    enabledByDefault?: boolean;
    file?: File;
    deleteFile?: boolean;
  };
  whatsappConfiguration: {
    enabledByDefault?: boolean;
    refFile?: File;
    deleteRefFile?: boolean;
    maskFile?: File;
    deleteMaskFile?: boolean;
  };
  flashEnabled?: boolean;
}

export interface Situations {
  name?: string;
  situations: { id: number; name: string; isNotEditable?: boolean }[];
}
interface ProductCodeDetail {
  id: number;
  name: string;
}
export interface Typology {
  id: number;
  productCodes: ProductCodeDetail[];
  name: string;
}

export interface TypologyForm {
  id?: number;
  productCodes: number[];
  name: string;
}
export interface ReasonCreationParams {
  name: string;
  translations: Pick<ReasonTranslation, 'name' | 'languageCode'>[];
}

export interface ReasonUpdateParams extends ReasonCreationParams {
  id: number;
}

export interface ProductCode {
  id?: number;
  name: string;
}

// HelpInfos (Phrases types) -----------
export interface HelpInfosFilter {
  SubFamily?: string;
  Content?: string;
  SituationIDs?: number[];
  IsExactFilterSubFamily?: boolean;
  selectedServiceTypes?: number[];
  serviceTypesGroupingMode?: number;
  pageNumber?: number;
  maxResultPage?: number;
}
export interface HelpInfosParams extends Omit<HelpInfosFilter, 'selectedServiceTypes' | 'serviceTypesGroupingMode'> {
  isExactFilterServiceType?: boolean;
  serviceTypeIDs?: number[];
}
export interface HelpInfos {
  maxPages?: number;
  pageNumber?: number;
  maxResultPage?: number;
  result?: HelpInfo[];
}
export interface HelpInfo {
  id: number;
  longMessage: string;
  longMessageBrut: string;
  shortMessage: string;
  subFamily: string;
  situations: Situation[];
  serviceTypeIDs?: number[];
}
export interface CreateHelpInfoParams {
  shortMessage: string;
  longMessageBrut: string;
  longMessage: string;
  subFamily: string;
  situationsIDs: number[];
  serviceTypeIDs: number[];
}
export interface UpdateHelpInfoParams extends CreateHelpInfoParams {
  id: number;
}
// --------------------------------------

// CompensationSentences (Phrases dedomagement) -----------

export interface CompensationSentenceParams {
  id: number;
  longMessage: string;
}

export interface CompensationSentence {
  id: number;
  name: string;
  longMessage: string;
  order: number;
  delayMax?: number;
  delayMin?: number;
}
// --------------------------------------

export interface Station {
  uicStationCode: string;
  name: string;
  shortCode: string;
}

export type StationsServed = Station & {
  sequence: number;
};

export type BasicFormCheckboxData = {
  label?: string;
  value?: string;
  disabled?: boolean;
};

export type ProcessusType = 'pre-operational' | 'operational' | 'post-operational';

export type SituationCategory = 'PRE_OP' | 'OP' | 'POST_OP';

export type BusinessCaseType =
  | 'OP-CANCELLATION'
  | 'OP-DELAY'
  | 'OP-DIVERSION'
  | 'OP-OTHER'
  | 'OP-PLATEFORM'
  | 'OP-STATION_LIMITATION'
  | 'POST-OP-DELAY'
  | 'POST-OP-NORMAL'
  | 'PRE-OP-CANCELLATION'
  | 'PRE-OP-DELAY'
  | 'PRE-OP-DIVERSION'
  | 'PRE-OP-OTHER'
  | 'PRE-OP-STATION_LIMITATION';

export type BusinessCase = {
  id: number;
  Name: string;
  businessCases: BusinessCaseItem[];
};
export type BusinessCases = BusinessCase[];

export type BusinessCaseItem = {
  id: number;
  Name: string;
  category: { id: number; Name: string };
};

export type Carriage = {
  carriageNumber: number;
  wholeCarriageSelected: boolean;
  upStage: boolean | null;
  downStage: boolean | null;
};

export interface StationsTemplatesPreview extends MinimalResponse {
  stationsTemplates: StationTemplates[];
}

export type StationTemplates = {
  stationUicCode?: string;
  stationShortCode?: string;
  emailTemplate?: EmailStationTemplate;
  whatsAppTemplate?: StationTemplate;
  smsTemplate?: StationTemplate;
};

export type StationTemplate = {
  body?: string;
};

export type EmailStationTemplate = StationTemplate & { object?: string };

export type CommunicationContent = {
  stationShortCode?: string;
  uicStation?: string;
  delay?: string;
  principalZoneShortMessage?: string;
  principalZoneLongMessage: string;
  secondaryZoneShortMessage?: string;
  secondaryZoneLongMessage: string;
  dock?: string;
  newSchedule?: string;
  departureTime?: string;
  arrivalTime?: string;
  stationReplacement?: string;
  compensationLongMessage?: string;
  compensationId?: number;
  compensationSelected?: boolean;
};

export interface SendCommunicationFirstStepForm {
  customersSelection: SendCommunicationCustomersSelection;
  impactedStations: string[];
  excludedStations: string[];
  selectedCarriages: Omit<Carriage, 'wholeCarriageSelected'>[];
  selectedTypologiesIds: number[];
  includePassengersWithoutSeats: boolean;
  selectedConflictTypeId?: ConflictType['id'];
  excludeAllConflictTypes: boolean;
  selectedPurchaseComparison: SendCommunicationPurchaseComparisonValues | undefined;
  purchaseReferenceDate: string | undefined;
  purchaseReferenceTime: string | undefined;
  DVNumbers: string | undefined;
}

export interface SendCommunicationSecondStepForm {
  situationId?: number;
  reasonId?: number;
  otherReasonComplementary?: string;
  communicationChannels?: SendCommunicationChannels[];
  mailObject?: string;
  mailHeadband?: string;
  communicationContent?: CommunicationContent[];
  signature?: string;
  compensationSelected?: boolean;
  isDifferedCommunication?: boolean;
  differedDaysBeforeCirculation?: number;
  differedCommunicationTime?: string;
}

export type SendCommunicationContent = {
  stationShortCode?: string;
  uicStation?: string;
  delay?: string;
  area1Short?: string;
  area1Long?: string;
  area2Short?: string;
  area2Long?: string;
  dock?: string;
  newSchedule?: string;
  stationReplacement?: string;
  compensationLongMessage?: string;
  compensationId?: number;
  compensationSelected?: boolean;
};

export interface SendCommunicationFormValues {
  isBulkCommunication?: boolean;
  maxDaysBeforeDeferredSending?: number;
  situationId?: number;
  serviceName?: string; // for unitary communication used in prepareFetchStationsTemplatesPreviewParams
  serviceDate?: string; // for unitary communication used in prepareFetchStationsTemplatesPreviewParams
  selectedServices?: ServiceInfo[];
  customersSelection?: SendCommunicationCustomersSelection;
  emailObject?: string;
  emailHeader?: string;
  sender?: string; // active user fullname
  author?: string; // means signature
  excludedStations?: string[];
  impactedStations?: string[];
  selectedPassengerTypologiesIds?: number[];
  conflictTypeId?: number;
  excludeAllConflictTypes: boolean;
  selectedCarriages?: Omit<Carriage, 'wholeCarriageSelected'>[];
  includePassengersWithoutSeats?: boolean;
  reasonId?: number;
  otherReason?: string;
  communicationChannels?: SendCommunicationChannels[];
  communicationContent?: SendCommunicationContent[];
  isDifferedCommunication?: boolean;
  differedCommunicationProperties?: {
    daysBeforeCirculation?: number;
    communicationTime?: string;
  };
  selectedPurchase?: {
    purchaseDateTimeComparison: SendCommunicationPurchaseComparisonValues;
    purchaseReferenceDateTime: string;
  };
  specificBookings?: string[];
  overviewOD?: string[];
}

export type SendCommunicationFormLabels =
  | 'clientsType'
  | 'impactedStations'
  | 'excludedStations'
  | 'selectedCarriages'
  | 'Others';

export type StepTwoFieldName = 'impactedStations' | 'excludedStations' | 'selectedCarriages';

export type Field =
  | 'reason'
  | 'otherReason'
  | 'newSchedule'
  | 'station'
  | 'prise'
  | 'detail'
  | 'compensation'
  | 'communicationChannels';

export type StationServedSegment = { label?: string; value?: string };
export interface Channels {
  id: number;
  Name: string;
}

export interface CommunicationsHistoryForm {
  serviceName?: string;
  station?: string;
  sender?: string;
  communicationStartDate?: string;
  communicationEndDate?: string;
  arrivalTime?: string;
  departureTime?: string;
  pageNumber?: number;
  maxResultPage?: number;
  situationIds?: number[];
  sendStatus?: number;
  parameterToSort?: string;
  ascDesc?: 'ASC' | 'DESC';
  isDifferedCommunication?: boolean;
  serviceTypeIDs?: number[];
}

export interface CommunicationsPendingHistoryForm {
  serviceName?: string;
  departureTime?: string;
  arrivalTime?: string;
  creationStartDate?: string;
  creationEndDate?: string;
  situationIds?: number[];
  sender?: string;
  pageNumber?: number;
  maxResultPage?: number;
}

export interface CommunicationsHistory {
  communicationID?: number;
  creationDate?: string;
  communicationContentID?: number;
  sentDateTime?: string;
  communicationSchedule?: string;
  processName?: string;
  situationName?: string;
  situationId?: number;
  customerSelection?: string;
  specificBookings?: string;
  author?: string;
  sender?: string;
  serviceName?: string;
  serviceDate?: string;
  station: Station | null;
  detail?: string;
  delay?: string;
  newSchedule?: string;
  dock?: string;
  stationReplacement?: string;
  communicationObject?: string;
  communicationHeader?: string;
  departureTime?: string;
  arrivalTime?: string;
  // TODO the status will need to be updated  when the mdw has decided on the right format
  emailStatus?: number | null;
  whatsAppStatus?: number | null;
  smsStatus?: number | null;
  motif?: string;
  principalZoneShortMessage?: string;
  principalZoneLongMessage?: string;
  secondaryZoneShortMessage?: string;
  secondaryZoneLongMessage?: string;
  passengerTypologies?: string | null;
  communicationChannels?: string[] | null;
  selectedCarriages: string;
  includePassengersWithoutSeats?: boolean;
  impactedStations: Station[];
  excludedStations: Station[];
  compensation?: string | null;
  compensationLongMessage?: string | null;
  compensationSelected?: string | null;
  conflictTypeName?: string | null;
  isDifferedCommunication?: boolean;
  other?: string;
  concernedPassengers?: number;
  overviewOD?: string[];
}

export interface PendingCommunicationsHistory {
  communicationID: number;
  processName?: string;
  serviceName: string;
  serviceDate: string;
  customerSelection?: string;
  creationDate: string;
  situationName?: string;
  situationId?: number;
  communicationSchedule?: string;
  daysBeforeCirculation?: number;
  communicationTime?: string;
  sender: string;
  contents?: CommunicationsHistory[];
}

export interface HistoricCommunication {
  result?: CommunicationsHistory[];
  pageNumber?: number;
  maxResultPage?: number;
  maxPages?: number;
}

export interface HistoricPendingCommunication {
  result?: PendingCommunicationsHistory[];
  pageNumber?: number;
  maxResultPage?: number;
  maxPages?: number;
}
export interface CustomerSelection {
  id: number;
  value: string;
}
export interface ApiVersion {
  version: string;
}

export interface ServiceInfo {
  serviceDate?: string;
  serviceName?: string;
}

export interface ServicesPassengersConcernedByCommunicationParams {
  selectedServices: ServiceInfo[];
  selectedClients: SendCommunicationCustomersSelection;
  excludedStations?: string[];
  impactedStations?: string[];
  selectedCarriages?: Omit<Carriage, 'wholeCarriageSelected'>[];
  includePassengersWithoutSeats: boolean;
  selectedPassengerTypologiesIds?: number[];
  conflictTypes?: number[];
  excludeAllConflictTypes: boolean;
  selectedPurchase?: {
    purchaseDateTimeComparison: SendCommunicationPurchaseComparisonValues;
    purchaseReferenceDateTime: string;
  };
  specificBookings?: string[];
}

export interface ServicesPassengersConcernedByCommunicationResponse extends MinimalResponse {
  totalPassengers?: number;
}

interface CommunicationContents {
  area1: string;
  area2: string;
  area3: string;
  area4: string;
  delay: string;
  newSchedule: string;
  uicStation?: string;
  stationReplacement: string;
  compensationId: number;
  communicationChannels: number[];
  otherReason: string;
  communicationObject?: string;
  communicationHeader?: string;
  reasonId: number;
  communication?: {
    situationId?: number;
    communicationHeader: string;
    communicationObject: string;
  };
  excludedStations: string[];
  id: null;
  impactedStations: string[];
  selectedCarriages: string[];
  serviceDate: string;
  author: string;
  situationId?: number;
}
export interface LatestCommunication {
  author: string;
  situationId?: number;
  communicationObject?: string;
  communicationHeader?: string;
  communicationContents: CommunicationContents[];
  customersSelection?: 0 | 1 | 2;
  customerSelectionId?: 0 | 1 | 2;
  excludedStations: string[];
  id: null;
  impactedStations: string[];
  selectedCarriages: string[];
  serviceDate: string;
  serviceName: string;
  communicationStatusId?: number;
}

export interface UserFilter {
  firstName?: string;
  lastName?: string;
  trainTypeIDs?: string[];
  serviceTypesGroupingMode?: number;
  profileID?: string;
  groupName?: string;
  pageNumber?: number;
  maxResultPage?: number;
}

export interface UserFilterParams extends Omit<UserFilter, 'serviceTypesGroupingMode'> {
  isExactFilterServiceType?: boolean;
}

export interface MessageMask {
  id?: number;
  situationName?: string;
  situationId?: number;
  hasReason?: boolean;
  hasVolumeDelay?: boolean;
  hasSubstituteStation?: boolean;
  hasDock?: boolean;
  hasPrincipalZoneLongMessage?: boolean;
  hasPrincipalZoneShortMessage?: boolean;
  hasSecondaryZoneLongMessage?: boolean;
  hasSecondaryZoneShortMessage?: boolean;
  hasCompensationZoneForAll?: boolean;
  hasCompensationZoneForUp?: boolean;
  hasCompensationZoneForDown?: boolean;
  hasCompensationZoneForUpOrDown?: boolean;
  hasCompensationZoneForSpecificBookings?: boolean;
  hasSigningZone?: boolean;
  signingContent?: string;
}

export type AcceptedLocale = 'es-ES' | 'fr-FR';

export type RepositoryItem = { BusinessCaseId: number };

export type RepositoryTableBusinessCase = {
  [key in BusinessCaseType]?: RepositoryItem[];
};

export type RepositoryTableList = {
  [key in ProcessusType]?: RepositoryTableBusinessCase;
};

export type RepositoryTable = {
  process: string;
  data: RepositoryTableBusinessCase;
}[];

export type SectionName =
  | 'Circulations'
  | 'Historisation'
  | 'Customer-Flux'
  | 'Communication'
  | 'Deffered-Communications'
  | 'Reasons'
  | 'Help-Info'
  | 'Typologies'
  | 'Situations'
  | 'Profiles'
  | 'Users';

export type Section = {
  id: number;
  name: SectionName;
  read: boolean;
  write: boolean;
  readLabel?: string | null;
  writeLabel?: string | null;
};

export interface ConflictType {
  id: number;
  name: string;
  isEnabled: boolean;
}

export type SaveCommunicationError = {
  hasError: boolean | undefined;
  message: string | undefined;
};

export type ServiceType = {
  id: number;
  serviceTypeName: string;
  serviceTypeCode: string;
};
